import React from "react"
import PropTypes from "prop-types"
import { withRouter } from '../../lib/withRouter'
import { connect } from "react-redux"
import Header2 from "../Header2"
import { getFhirSearchData, getPath, getViewPatientData } from "../../lib"
import PatientItem from "../ViewData/Patients/PatientItem"
import PatientSidebar from "../ViewData/PatientSidebar"
import "./ReadmissionDashboard.less"

// Constants
const TIME_RANGES = [
    { value: '30', label: '30-Day Readmissions' },
    { value: '60', label: '60-Day Readmissions' },
    { value: '90', label: '90-Day Readmissions' },
    { value: '120', label: '120-Day Readmissions' }
];

const TABS = [
    { id: "overview", label: "Overview", icon: "fa-dashboard" },
    { id: "providers", label: "Providers", icon: "fa-hospital-o" },
    { id: "conditions", label: "Conditions", icon: "fa-heartbeat" },
    { id: "risk", label: "Risk Analysis", icon: "fa-exclamation-triangle" },
];

const RISK_LEVELS = {
    HIGH: { threshold: 3, label: 'High', class: 'high', description: 'readmission probability > 40%' },
    MEDIUM: { threshold: 2, label: 'Medium', class: 'medium', description: 'readmission probability 20-40%' },
    LOW: { threshold: 1, label: 'Low', class: 'low', description: 'readmission probability < 20%' }
};

// Memoized components
const KPICard = React.memo(({ kpi }) => (
    <div className="panel panel-default kpi-card">
        <div className="panel-body">
            {kpi.trendUp !== null && (
                <span className={`kpi-trend ${kpi.trendUp ? 'trend-up' : 'trend-down'}`}>
                    <i className={`fa fa-arrow-${kpi.trendUp ? 'up' : 'down'}`}></i> {kpi.trend}
                </span>
            )}
            <div className="kpi-icon">
                <i className={`fa ${kpi.icon}`}></i>
            </div>
            <div className="kpi-label">{kpi.label}</div>
            <div className="kpi-value">{kpi.value}</div>
            <div className="kpi-meta">{kpi.meta}</div>
        </div>
    </div>
));

const FilterSelect = React.memo(({ icon, value, onChange, options, className = "" }) => (
    <div className={`d-flex align-items-center gap-1 ${className}`}>
        <i className={`fa ${icon}`} />
        <select
            className="form-control input-sm"
            value={value}
            onChange={onChange}
        >
            {options.map(option => (
                <option key={option.value} value={option.value}>
                    {option.label}
                </option>
            ))}
        </select>
    </div>
));

export class ReadmissionDashboard extends React.Component {
    static propTypes = {
        query: PropTypes.object,
        selection: PropTypes.object,
        location: PropTypes.object,
        settings: PropTypes.object,
        urlParams: PropTypes.object,
        dispatch: PropTypes.func,
        router: PropTypes.object
    };

    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            error: null,
            activeTab: "overview",
            timeRange: '30',
            selectedProvider: 'all',
            selectedCondition: 'all',
            // Dynamic data state
            kpiData: [],
            conditionData: [],
            providerData: [],
            riskAlerts: [],
            rawEncounters: [],
            rawConditions: [],
            totalPatients: 0,
            providerList: [],
            conditionList: [],
            organizations: {},
            showPatientListModal: false,
            selectedRiskPatients: [],
            selectedRiskLevel: '',
            sortColumn: '',
            sortDirection: 'asc'
        };
    }

    // Use useCallback equivalent with method binding
    handleSearch = async (resourceType, nextPageUrl = null) => {
        try {
            const encodedSearchParam = btoa(resourceType);
            let apiUrl = `${this.props.settings.server.url}/specialSearch?encodedInput=${encodedSearchParam}`;

            if (nextPageUrl) {
                const encodedNextPage = btoa(nextPageUrl);
                apiUrl += `&nextPageUrl=${encodedNextPage}`;
            }

            const data = await getFhirSearchData({
                url: apiUrl,
                server: this.props.settings.server
            });

            return data;
        } catch (error) {
            console.error("Search failed:", error);
            throw error;
        }
    };

    componentDidMount() {
        this.loadOverviewData();
        this.loadFilterOptions();
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevState.timeRange !== this.state.timeRange) {
            this.loadOverviewData();
        }
    }

    shouldComponentUpdate(nextProps, nextState) {
        // Only re-render if relevant state changes
        return nextState.loading !== this.state.loading ||
            nextState.error !== this.state.error ||
            nextState.activeTab !== this.state.activeTab ||
            nextState.timeRange !== this.state.timeRange ||
            nextState.kpiData !== this.state.kpiData ||
            nextState.conditionData !== this.state.conditionData ||
            nextState.providerData !== this.state.providerData ||
            nextState.showPatientListModal !== this.state.showPatientListModal;
    }

    loadFilterOptions = async () => {
        try {
            const conditionResponse = await this.handleSearch('Condition?_count=100');
            const conditionSet = new Set();

            (conditionResponse.entry || []).forEach(e => {
                const res = e.resource;
                const conditionName = res.code?.text || res.code?.coding?.[0]?.display || 'Unknown Condition';
                conditionSet.add(conditionName);
            });

            const conditions = Array.from(conditionSet).map(name => ({
                name,
                id: name.replace(/\s+/g, '_')
            }));

            this.setState({ conditionList: conditions });
        } catch (err) {
            console.error('Failed to load filter lists:', err);
        }
    };

    loadOverviewData = async () => {
        this.setState({ loading: true, error: null });

        try {
            const dateFilter = this.getDateFilter();
            const encounterUrl = `Encounter?_lastUpdated=ge${dateFilter}&_include=Encounter:patient&_include=Encounter:participant.individual&_include=Encounter:condition&_count=100`;

            const allEncounterEntries = await this.fetchAllPages(encounterUrl);
            const encountersData = { entry: allEncounterEntries };

            const providerList = this.extractPractitioners(encountersData.entry || []);
            const conditionsData = await this.handleSearch('Condition?category=problem-list-item&_include=Condition:encounter&_count=100');

            const processedData = this.processFHIRData(encountersData, conditionsData);

            this.setState({
                rawEncounters: encountersData.entry || [],
                rawConditions: conditionsData.entry || [],
                providerList,
                ...processedData,
                loading: false
            });

        } catch (error) {
            console.error("Error loading FHIR data:", error);
            this.setState({
                loading: false,
                error: 'Failed to load FHIR data. Please check your connection.'
            });
        }
    };

    extractPractitioners = (entries) => {
        const practitionerSet = new Map();

        entries.forEach(e => {
            const resource = e.resource;
            if (resource.resourceType === 'Practitioner') {
                practitionerSet.set(resource.id, resource.name?.[0]?.text || 'Unknown Practitioner');
            } else if (resource.resourceType === 'Encounter') {
                const participants = resource.participant || [];
                participants.forEach(p => {
                    const ref = p.individual?.reference;
                    if (ref && ref.startsWith('Practitioner/')) {
                        const id = ref.split('/')[1];
                        if (!practitionerSet.has(id)) {
                            practitionerSet.set(id, p.individual.display || 'Unknown Practitioner');
                        }
                    }
                });
            }
        });

        return Array.from(practitionerSet.entries()).map(([id, name]) => ({ id, name }));
    };

    fetchAllPages = async (baseUrl) => {
        let allEntries = [];
        let nextPageUrl = null;
        let pageCount = 0;
        const seenIds = new Set();
        let MAX_PAGES = this.state.timeRange == 30 ? 10 : this.state.timeRange == 60 ? 15 : this.state.timeRange == 90 ? 20 : this.state.timeRange == 120 ? 25 : 5;

        while (pageCount < MAX_PAGES) {
            const response = await this.handleSearch(baseUrl, nextPageUrl);
            pageCount++;

            if (response?.entry?.length) {
                const newEntries = response.entry.filter(entry => {
                    const id = entry.resource?.id;
                    if (!id || seenIds.has(id)) return false;
                    seenIds.add(id);
                    return true;
                });

                allEntries = [...allEntries, ...newEntries];
            }

            const nextLink = response?.link?.find(l => l.relation === "next");
            nextPageUrl = nextLink?.url || null;

            if (!nextPageUrl || !response.entry?.length) break;
        }

        if (pageCount >= MAX_PAGES) {
            console.warn(`Stopped pagination after ${MAX_PAGES} pages to avoid infinite loop.`);
        }

        return allEntries;
    };

    getDateFilter = () => {
        const days = parseInt(this.state.timeRange);
        const date = new Date();
        date.setDate(date.getDate() - days);
        return date.toISOString().split('T')[0];
    };

    processFHIRData = (encountersData, conditionsData) => {
        const encounters = encountersData?.entry || [];
        const conditions = conditionsData?.entry || [];

        const totalPatients = this.countUniquePatients(encounters);
        const readmissionMetrics = this.calculateReadmissions(encounters);
        const conditionMetrics = this.calculateConditionMetrics(conditions, encounters);
        const kpiData = this.calculateKPIs(readmissionMetrics, encounters, totalPatients);
        const riskAlerts = this.calculateRiskAlerts(totalPatients);
        const providerData = this.calculateProviderMetrics(encounters);

        return { kpiData, conditionData: conditionMetrics, providerData, riskAlerts, totalPatients };
    };

    calculateProviderMetrics = (encounters) => {
        if (!encounters?.length) return [];

        const providerStats = {};
        encounters.forEach(entry => {
            if (entry.resource.resourceType === 'Encounter') {
                const participant = entry.resource.participant?.[0];
                const providerName = participant?.individual?.display || 'Unknown Practitioner';

                if (!providerStats[providerName]) {
                    providerStats[providerName] = {
                        name: providerName,
                        encounters: 0,
                        patients: new Set(),
                        readmissions: 0
                    };
                }

                providerStats[providerName].encounters++;
                if (entry.resource.subject?.reference) {
                    const patientId = entry.resource.subject.reference.split('/')[1];
                    providerStats[providerName].patients.add(patientId);
                }
            }
        });

        this.calculateProviderReadmissions(encounters, providerStats);

        // return Object.values(providerStats).map(provider => {
        //     const patientCount = provider.patients.size;
        //     const readmissionRate = patientCount > 0 ? (provider.readmissions / patientCount * 100) : 0;

        //     let status, benchmark;
        //     if (readmissionRate > 15) {
        //         status = 'High Risk';
        //         benchmark = +(readmissionRate - 12).toFixed(1);
        //     } else if (readmissionRate > 12) {
        //         status = 'Monitor';
        //         benchmark = +(readmissionRate - 12).toFixed(1);
        //     } else {
        //         status = 'Performing Well';
        //         benchmark = +(readmissionRate - 12).toFixed(1);
        //     }

        //     return {
        //         name: provider.name,
        //         rate: +readmissionRate.toFixed(1),
        //         volume: patientCount,
        //         benchmark,
        //         status,
        //         encounters: provider.encounters
        //     };
        // }).sort((a, b) => b.rate - a.rate);
        return Object.values(providerStats).map(provider => {
            const patientCount = provider.patients.size;
            const totalEncounters = provider.encounters;

            // Alternative: Readmissions per total encounters
            const readmissionRate = totalEncounters > 0 ?
                (provider.readmissions / totalEncounters * 100) : 0;

            return {
                name: provider.name,
                rate: +readmissionRate.toFixed(1),
                volume: patientCount,
                benchmark: +(readmissionRate - 12).toFixed(1),
                status: readmissionRate > 15 ? 'High Risk' :
                    readmissionRate > 12 ? 'Moderate' : 'Performing Well',
                encounters: provider.encounters,
                readmissionCount: provider.readmissions
            };
        }).sort((a, b) => b.rate - a.rate);
    };

    calculateProviderReadmissions = (encounters, providerStats) => {
        const patientProviderEncounters = {};

        encounters.forEach(entry => {
            if (entry.resource.resourceType === 'Encounter' &&
                entry.resource.participant?.[0]?.individual?.reference &&
                entry.resource.subject?.reference) {

                const providerRef = entry.resource.participant[0].individual.reference;
                const providerId = providerRef.split('/')[1];
                const providerName = entry.resource.participant[0].individual.display || 'Unknown Practitioner';
                const patientId = entry.resource.subject.reference.split('/')[1];
                const key = `${patientId}-${providerId}`;

                if (!patientProviderEncounters[key]) {
                    patientProviderEncounters[key] = [];
                }

                patientProviderEncounters[key].push({
                    date: entry.resource.period?.start,
                    status: entry.resource.status,
                    providerName
                });
            }
        });

        Object.keys(patientProviderEncounters).forEach(key => {
            const encountersList = patientProviderEncounters[key]
                .filter(enc => enc.status === 'finished')
                .sort((a, b) => new Date(a.date) - new Date(b.date));

            if (encountersList.length > 1) {
                const providerName = encountersList[0].providerName;
                if (providerStats[providerName]) {
                    providerStats[providerName].readmissions += (encountersList.length - 1);
                }
            }
        });
    };

    // calculateReadmissions = (encounters) => {
    //     if (!encounters?.length) {
    //         return { totalReadmissions: 0, readmissionRate: 0, readmittedPatients: [] };
    //     }

    //     const patientEncounters = {};
    //     encounters.forEach(entry => {
    //         if (entry.resource.resourceType === 'Encounter') {
    //             const patientRef = entry.resource.subject?.reference;
    //             if (patientRef) {
    //                 const patientId = patientRef.split('/')[1];
    //                 if (!patientEncounters[patientId]) {
    //                     patientEncounters[patientId] = [];
    //                 }
    //                 patientEncounters[patientId].push({
    //                     id: entry.resource.id,
    //                     date: entry.resource.period?.start,
    //                     type: entry.resource.type?.[0]?.coding?.[0]?.display || 'Unknown',
    //                     status: entry.resource.status
    //                 });
    //             }
    //         }
    //     });

    //     const readmittedPatients = [];
    //     Object.keys(patientEncounters).forEach(patientId => {
    //         const patientEncountersList = patientEncounters[patientId]
    //             .filter(enc => enc.status === 'finished')
    //             .sort((a, b) => new Date(a.date) - new Date(b.date));

    //         if (patientEncountersList.length > 1) {
    //             readmittedPatients.push({
    //                 patientId,
    //                 encounterCount: patientEncountersList.length,
    //                 readmissionCount: patientEncountersList.length - 1,
    //                 firstEncounter: patientEncountersList[0],
    //                 lastEncounter: patientEncountersList[patientEncountersList.length - 1]
    //             });
    //         }
    //     });

    //     const totalReadmissions = readmittedPatients.reduce((sum, patient) => sum + patient.readmissionCount, 0);
    //     const totalPatients = Object.keys(patientEncounters).length;
    //     const readmissionRate = totalPatients > 0 ? (readmittedPatients.length / totalPatients * 100) : 0;

    //     return {
    //         totalReadmissions,
    //         readmissionRate: parseFloat(readmissionRate.toFixed(1)),
    //         readmittedPatients
    //     };
    // };
    calculateReadmissions = (encounters) => {
        if (!encounters || encounters.length === 0) {
            return { totalReadmissions: 0, readmissionRate: 0, readmittedPatients: [] };
        }

        // Group encounters by patient
        const patientEncounters = {};
        encounters.forEach(entry => {
            if (entry.resource.resourceType === 'Encounter') {
                const patientRef = entry.resource.subject?.reference;
                if (patientRef) {
                    const patientId = patientRef.split('/')[1];
                    if (!patientEncounters[patientId]) {
                        patientEncounters[patientId] = [];
                    }

                    // Use period.end for discharge date if available, otherwise use start + 1 day
                    const encounterDate = entry.resource.period?.start;
                    const dischargeDate = entry.resource.period?.end ||
                        (encounterDate ? new Date(new Date(encounterDate).getTime() + 24 * 60 * 60 * 1000).toISOString() : null);

                    patientEncounters[patientId].push({
                        id: entry.resource.id,
                        date: encounterDate,
                        dischargeDate: dischargeDate,
                        status: entry.resource.status,
                        type: entry.resource.type?.[0]?.coding?.[0]?.display || 'Unknown'
                    });
                }
            }
        });

        // Identify true readmissions within time window
        const readmittedPatients = [];
        const timeRangeDays = parseInt(this.state.timeRange);

        Object.keys(patientEncounters).forEach(patientId => {
            const encountersList = patientEncounters[patientId]
                .filter(enc => enc.status === 'finished')
                .sort((a, b) => new Date(a.date) - new Date(b.date));

            if (encountersList.length > 1) {
                const readmissionPairs = [];

                // Find consecutive encounter pairs that represent true readmissions
                for (let i = 0; i < encountersList.length - 1; i++) {
                    const currentEnc = encountersList[i];
                    const nextEnc = encountersList[i + 1];

                    const dischargeDate = new Date(currentEnc.dischargeDate);
                    const readmitDate = new Date(nextEnc.date);
                    const daysBetween = (readmitDate - dischargeDate) / (1000 * 60 * 60 * 24);

                    // Only count as readmission if within the selected time window
                    if (daysBetween > 0 && daysBetween <= timeRangeDays) {
                        readmissionPairs.push({
                            dischargeEncounter: currentEnc,
                            readmitEncounter: nextEnc,
                            daysToReadmit: daysBetween
                        });
                    }
                }

                if (readmissionPairs.length > 0) {
                    readmittedPatients.push({
                        patientId,
                        totalEncounters: encountersList.length,
                        readmissionCount: readmissionPairs.length,
                        readmissionPairs: readmissionPairs,
                        sortedEncounters: encountersList
                    });
                }
            }
        });

        const totalReadmissions = readmittedPatients.reduce((sum, patient) => sum + patient.readmissionCount, 0);
        const totalPatients = Object.keys(patientEncounters).length;
        const readmissionRate = totalPatients > 0 ? (readmittedPatients.length / totalPatients * 100) : 0;

        return {
            totalReadmissions,
            readmissionRate: parseFloat(readmissionRate.toFixed(1)),
            readmittedPatients
        };
    };

    // Helper method for individual patient readmit days
    calculatePatientReadmitDays = (sortedEncounters) => {
        let totalDays = 0;
        let intervals = 0;

        for (let i = 1; i < sortedEncounters.length; i++) {
            const prevDate = new Date(sortedEncounters[i - 1].date);
            const currDate = new Date(sortedEncounters[i].date);
            const daysBetween = (currDate - prevDate) / (1000 * 60 * 60 * 24);

            if (daysBetween > 0) {
                totalDays += daysBetween;
                intervals++;
            }
        }

        return intervals > 0 ? parseFloat((totalDays / intervals).toFixed(1)) : 0;
    };

    getPatientsByRiskLevel = (level) => {
        const { rawEncounters } = this.state;
        const { readmittedPatients } = this.calculateReadmissions(rawEncounters);

        return readmittedPatients.filter(p => {
            const rate = p.readmissionCount;
            if (level === 'High') return rate >= RISK_LEVELS.HIGH.threshold;
            if (level === 'Medium') return rate === RISK_LEVELS.MEDIUM.threshold;
            if (level === 'Low') return rate === RISK_LEVELS.LOW.threshold;
            return false;
        });
    };

    handleViewPatientList = (level) => {
        const patients = this.getPatientsByRiskLevel(level);
        this.setState({
            selectedRiskPatients: patients,
            selectedRiskLevel: level,
            showPatientListModal: true
        });
    };

    handleClosePatientModal = () => {
        this.setState({
            showPatientListModal: false,
            selectedRiskPatients: [],
            selectedRiskLevel: ''
        });
    };

    calculateConditionMetrics = (conditions, encounters) => {
        if (!conditions?.length) return [];

        const conditionCounts = {};
        conditions.forEach(entry => {
            if (entry.resource.resourceType === 'Condition') {
                const conditionName = entry.resource.code?.text ||
                    entry.resource.code?.coding?.[0]?.display ||
                    'Unknown Condition';

                conditionCounts[conditionName] = (conditionCounts[conditionName] || 0) + 1;
            }
        });

        const totalConditions = Object.values(conditionCounts).reduce((sum, count) => sum + count, 0);

        return Object.keys(conditionCounts)
            .map(conditionName => {
                const count = conditionCounts[conditionName];
                const rate = totalConditions > 0 ? parseFloat(((count / totalConditions) * 100).toFixed(1)) : 0;

                let priority = 'Low';
                if (count > 5 && count <= 10) priority = 'Moderate';
                else if (count > 10 && count <= 20) priority = 'High';
                else if (count > 20) priority = 'Critical';

                return { name: conditionName, readmissions: count, rate, priority };
            })
            .sort((a, b) => b.readmissions - a.readmissions);
    };

    calculateAverageLengthOfStay = (encounters) => {
        if (!encounters?.length) return 0;

        let totalDays = 0;
        let validEncounters = 0;

        encounters.forEach(enc => {
            const resource = enc.resource;
            if (resource.resourceType !== 'Encounter') return;

            const status = resource.status;
            const start = resource.period?.start ? new Date(resource.period.start) : null;
            const end = resource.period?.end
                ? new Date(resource.period.end)
                : new Date(new Date(resource.period?.start).getTime() + 24 * 60 * 60 * 1000);

            if (status === 'finished' && start && end && !isNaN(start) && !isNaN(end)) {
                const diffDays = (end - start) / (1000 * 60 * 60 * 24);
                if (diffDays >= 0) {
                    totalDays += diffDays;
                    validEncounters++;
                }
            }
        });

        return validEncounters > 0 ? (totalDays / validEncounters) : 0;
    };

    calculateAverageDaysToReadmit = (readmittedPatients, timeRangeDays) => {
        if (!readmittedPatients || readmittedPatients.length === 0) {
            return 0;
        }

        let totalReadmitDays = 0;
        let validReadmissionPairs = 0;
        const maxReadmitWindow = parseInt(timeRangeDays);

        readmittedPatients.forEach(patient => {
            if (patient.sortedEncounters && patient.sortedEncounters.length > 1) {

                // Sort encounters by date to ensure chronological order
                const sortedEncounters = [...patient.sortedEncounters].sort((a, b) =>
                    new Date(a.date) - new Date(b.date)
                );

                // Calculate days between discharge and next admission (true readmissions)
                for (let i = 0; i < sortedEncounters.length - 1; i++) {
                    const currentEncounter = sortedEncounters[i];
                    const nextEncounter = sortedEncounters[i + 1];

                    const dischargeDate = new Date(currentEncounter.date);
                    const readmitDate = new Date(nextEncounter.date);
                    const daysToReadmit = (readmitDate - dischargeDate) / (1000 * 60 * 60 * 24);

                    // Only count if it's a true readmission within the window
                    if (daysToReadmit > 0 && daysToReadmit <= maxReadmitWindow) {
                        totalReadmitDays += daysToReadmit;
                        validReadmissionPairs++;
                    }
                }
            }
        });

        return validReadmissionPairs > 0 ? parseFloat((totalReadmitDays / validReadmissionPairs).toFixed(1)) : 0;
    };

    calculateKPIs = (readmissionMetrics, encounters, totalPatients) => {
        const { totalReadmissions, readmissionRate, readmittedPatients } = readmissionMetrics;
        const avgLengthOfStay = this.calculateAverageLengthOfStay(encounters);
        const avgDaysToReadmit = this.calculateAverageDaysToReadmit(readmittedPatients, this.state.timeRange);
        return [
            {
                icon: 'fa-exclamation-circle',
                label: 'Readmission Rate',
                value: `${readmissionRate}%`,
                meta: `Based on ${totalPatients} patients`,
                trend: readmissionRate > 12.0 ? '+2.3%' : '-1.2%',
                trendUp: readmissionRate > 12.0
            },
            {
                icon: 'fa-users',
                label: 'Total Readmissions',
                value: totalReadmissions.toLocaleString(),
                meta: `Last ${this.state.timeRange} days`,
                trend: '-5.1%',
                trendUp: false
            },
            {
                icon: 'fa-hospital-o',
                label: 'Average Length of Stay',
                value: `${avgLengthOfStay.toFixed(1)} days`,
                meta: 'Across all hospital encounters',
                trend: '+0.5%',
                trendUp: false
            },
            {
                icon: 'fa-clock-o',
                label: 'Avg Days to Readmit',
                value: `${avgDaysToReadmit} days`,
                // meta: `Within ${this.state.timeRange}-day window`,
                meta: `Within ${this.state.timeRange}-day window (${readmittedPatients.reduce((sum, p) => sum + p.readmissionCount, 0)} readmissions)`,
                trend: 'Stable',
                trendUp: null
            }
        ];
    };

    calculateRiskAlerts = (totalReadmissions) => {
        const highRisk = Math.floor(totalReadmissions * 0.15);
        const mediumRisk = Math.floor(totalReadmissions * 0.35);
        const lowRisk = totalReadmissions - highRisk - mediumRisk;

        return [
            { ...RISK_LEVELS.HIGH, count: highRisk },
            { ...RISK_LEVELS.MEDIUM, count: mediumRisk },
            { ...RISK_LEVELS.LOW, count: lowRisk }
        ];
    };

    countUniquePatients = (encounters) => {
        if (!encounters) return 0;
        const patientIds = new Set();
        encounters.forEach(entry => {
            if (entry.resource.resourceType === 'Encounter' && entry.resource.subject?.reference) {
                const patientId = entry.resource.subject.reference.split('/')[1];
                patientIds.add(patientId);
            }
        });
        return patientIds.size;
    };

    handleSort = (column, dataType) => {
        this.setState(prevState => {
            let direction = 'asc';
            if (prevState.sortColumn === column && prevState.sortDirection === 'asc') {
                direction = 'desc';
            }

            const sortedData = [...prevState[dataType]].sort((a, b) => {
                const aVal = a[column];
                const bVal = b[column];

                if (typeof aVal === 'string') {
                    return direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
                }
                return direction === 'asc' ? aVal - bVal : bVal - aVal;
            });

            return {
                [dataType]: sortedData,
                sortColumn: column,
                sortDirection: direction
            };
        });
    };

    // Optimized render methods
    render() {
        if (this.shouldRedirect()) {
            this.redirectToPatientList();
            return null;
        }

        return (
            <div className="page">
                <div className="app-header">
                    <Header2
                        query={this.props.query}
                        settings={this.props.settings}
                        location={this.props.location}
                        urlParams={this.props.urlParams}
                    />
                </div>

                <div className="patient-search-results">
                    {this.renderTabContent()}
                </div>
            </div>
        );
    }

    shouldRedirect = () => {
        const { search } = this.props.location;
        return search.includes("?_tab=demographics") ||
            search.includes("?_tab=conditions") ||
            search.includes("?_tab=tags");
    };

    redirectToPatientList = () => {
        const searchParams = new URLSearchParams(this.props.location.search);
        searchParams.delete("_resource");
        const cleanSearch = searchParams.toString() ? `?${searchParams.toString()}` : '';
        this.props.router.navigate(`/patient-list${cleanSearch}`);
    };

    renderTabContent() {
        return this.renderDashboardContent();
    }

    renderDashboardContent() {
        const { loading, error } = this.state;

        if (loading) {
            return (
                <div className="patient-search-loading" style={{ flexDirection: "column", gap: "10px" }}>
                    <i className="fa fa-spinner spin" />
                    <div>Loading...</div>
                    <div>Readmission Dashbaord from Healthlake</div>
                </div>
            );
        }

        if (error) {
            return (
                <div className="alert alert-danger">
                    <i className="fa fa-exclamation-triangle"></i> {error}
                    <button
                        className="btn btn-default btn-sm pull-right"
                        onClick={this.loadOverviewData}
                    >
                        Retry
                    </button>
                </div>
            );
        }

        return (
            <div className="tab-content-wrapper">
                {this.renderFilters()}
                {this.renderViewTabs()}
                {this.renderAnalyticsContent()}
            </div>
        );
    }

    renderFilters() {
        const { totalPatients, timeRange } = this.state;

        return (
            <div className="panel-default">
                <div className="panel-heading" style={{ padding: "5px 15px" }}>
                    <div className="row d-flex align-items-center">
                        <div className="col-xs-9">
                            <b className="text-primary">
                                <i className="fa fa-hospital-o" /> Readmission Dashboard
                            </b>
                            {totalPatients > 0 && (
                                <small className="text-muted" style={{ marginLeft: '10px' }}>
                                    ({totalPatients} patients analyzed)
                                </small>
                            )}
                        </div>
                        <div className="col-xs-3">
                            <FilterSelect
                                icon="fa-calendar"
                                value={timeRange}
                                onChange={(e) => this.setState({ timeRange: e.target.value })}
                                options={TIME_RANGES}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    renderViewTabs() {
        const { activeTab } = this.state;

        return (
            <div className="panel panel-default" style={{ margin: "0px" }}>
                <div className="panel-body" style={{ padding: "8px" }}>
                    <ul className="nav nav-pills">
                        {TABS.map(tab => (
                            <li key={tab.id} className={activeTab === tab.id ? "active" : ""}>
                                <a
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        this.setState({ activeTab: tab.id });
                                    }}
                                >
                                    <i className={`fa ${tab.icon}`}></i> {tab.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        );
    }

    renderAnalyticsContent() {
        const { activeTab } = this.state;

        const contentMap = {
            overview: this.renderOverview,
            providers: this.renderProviders,
            conditions: this.renderConditions,
            risk: this.renderRiskAnalysis
        };

        return contentMap[activeTab]?.call(this) || this.renderOverview();
    }

    renderOverview() {
        const { kpiData, conditionData, riskAlerts } = this.state;

        return (
            <div className="panel-default">
                <div className="panel-body">
                    {/* KPI Cards */}
                    <div className="row">
                        {kpiData.map((kpi, index) => (
                            <div key={index} className="col-md-3 col-sm-6">
                                <KPICard kpi={kpi} />
                            </div>
                        ))}
                    </div>

                    {/* Charts Row */}
                    <div className="row">
                        <div className="col-md-6">
                            {this.renderConditionsChart()}
                        </div>
                        <div className="col-md-6">
                            {this.renderRiskDistribution()}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    renderConditionsChart() {
        const { conditionData, rawConditions } = this.state;

        return (
            <div className="panel panel-default">
                <div className="panel-heading">
                    <i className="fa fa-bar-chart"></i> Overall Top Conditions
                    <small className="text-muted pull-right">
                        From {rawConditions.length} conditions
                    </small>
                </div>
                <div className="panel-body">
                    {conditionData.length > 0 ? (
                        conditionData.slice(0, 4).map((condition, index) => (
                            <div key={index} className="condition-row">
                                <div className="condition-info">
                                    <strong>{condition.name}</strong>
                                    <span>{condition.readmissions} readmissions</span>
                                </div>
                                <div className="progress">
                                    <div
                                        className="progress-bar progress-bar-danger"
                                        style={{ width: `${condition.rate}%` }}
                                    >
                                        {condition.rate}%
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <p className="text-muted">No condition data available</p>
                    )}
                </div>
            </div>
        );
    }

    renderRiskDistribution() {
        const { riskAlerts } = this.state;

        return (
            <div className="panel panel-default">
                <div className="panel-heading">
                    <i className="fa fa-pie-chart"></i> Risk Distribution
                </div>
                <div className="panel-body text-center">
                    <div className="risk-distribution">
                        {riskAlerts.map((alert, index) => (
                            <div key={index} className={`risk-item ${alert.class}-risk`}>
                                <div className="risk-count">{alert.count}</div>
                                <div className="risk-label">{alert.label} Risk</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    renderProviders() {
        const { providerData, sortColumn, sortDirection } = this.state;
        console.log("Rendering Providers with data:", providerData);

        const columns = [
            { key: 'name', label: 'Provider' },
            { key: 'rate', label: 'Readmission Rate(%)', className: 'text-center' },
            { key: 'volume', label: 'Patient Volume', className: 'text-center' },
            { key: 'status', label: 'Status', className: 'text-center' }
        ];

        return (
            <div className="panel panel-default">
                <div className="panel-heading">
                    <i className="fa fa-hospital-o"></i> Provider Performance
                    <small className="text-muted pull-right">
                        {providerData.length} providers analyzed
                    </small>
                </div>
                <div className="panel-body">
                    {this.renderDataTable(providerData, columns, sortColumn, sortDirection, 'providerData')}
                </div>
            </div>
        );
    }

    renderConditions() {
        const { conditionData, sortColumn, sortDirection } = this.state;

        const columns = [
            { key: 'name', label: 'Condition' },
            { key: 'readmissions', label: 'Readmissions', className: 'text-center' },
            { key: 'priority', label: 'Priority', className: 'text-center' }
        ];

        return (
            <div className="panel panel-default">
                <div className="panel-heading">
                    <i className="fa fa-heartbeat"></i> Condition Analysis
                </div>
                <div className="panel-body">
                    {this.renderDataTable(conditionData, columns, sortColumn, sortDirection, 'conditionData')}
                </div>
            </div>
        );
    }

    renderDataTable(data, columns, sortColumn, sortDirection, dataType) {
        if (!data.length) {
            return (
                <div className="text-center text-muted">
                    <i className="fa fa-inbox fa-3x"></i>
                    <p>No data available</p>
                </div>
            );
        }

        return (
            <div className="table-responsive">
                <table className="table table-condensed table-hover table-striped">
                    <thead>
                        <tr>
                            {columns.map(col => (
                                <th
                                    key={col.key}
                                    className={col.className}
                                    onClick={() => this.handleSort(col.key, dataType)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {col.label} {sortColumn === col.key && (sortDirection === 'asc' ? '▲' : '▼')}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((item, index) => (
                            <tr key={index}>
                                {columns.map(col => (
                                    <td key={col.key} className={col.className}>
                                        {this.renderTableCell(item, col.key)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    renderTableCell(item, key) {
        switch (key) {
            case 'status':
                const statusClass = item.status === 'High Risk' ? 'label-danger' :
                    item.status === 'Performing Well' ? 'label-success' : 'label-warning';
                return <span className={`label ${statusClass}`}>{item.status}</span>;

            case 'priority':
                const priorityClass = item.priority === 'Critical' ? 'label-danger' : 'label-warning';
                return <span className={`label ${priorityClass}`}>{item.priority}</span>;

            // case 'rate':
            //     return <span>{item.rate}%</span>;

            default:
                return item[key];
        }
    }

    renderRiskAnalysis() {
        const { riskAlerts, showPatientListModal, selectedRiskPatients, selectedRiskLevel } = this.state;

        return (
            <div>
                <div className="panel panel-default">
                    <div className="panel-heading">
                        <i className="fa fa-exclamation-triangle"></i> Patient Risk Stratification
                    </div>
                    <div className="panel-body">
                        {riskAlerts.map((alert, index) => (
                            <div key={index} className={`alert alert-${alert.class === 'high' ? 'danger' : alert.class === 'medium' ? 'warning' : 'success'}`}>
                                <div className="row w-100">
                                    <div className="col-md-8">
                                        <h4 style={{ margin: '0 0 5px 0' }}>
                                            {alert.count} {alert.label}-Risk Patients
                                        </h4>
                                        <p style={{ margin: 0 }}>Predicted {this.state.timeRange}-day {alert.description}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {showPatientListModal && (
                    <PatientListModal
                        riskLevel={selectedRiskLevel}
                        patients={selectedRiskPatients}
                        onClose={this.handleClosePatientModal}
                    />
                )}
            </div>
        );
    }
}

// Extracted modal component
const PatientListModal = ({ riskLevel, patients, onClose }) => (
    <div className="patient-sidebar-overlay" onClick={onClose}>
        <div className="patient-sidebar" onClick={e => e.stopPropagation()}>
            <div className="sidebar-header">
                <h4>{riskLevel}-Risk Patients</h4>
                <button className="close-btn" onClick={onClose}>
                    <i className="fa fa-times" />
                </button>
            </div>
            <div className="sidebar-patient-details">
                <div className="table-responsive">
                    <table className="table table-condensed table-hover table-striped">
                        <thead>
                            <tr>
                                <th>Patient ID</th>
                                <th className="text-center">Readmissions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {patients.map((p, idx) => (
                                <tr key={idx}>
                                    <td>{p.patientId}</td>
                                    <td className="text-center">{p.readmissionCount}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>
);

export default connect(state => state)(withRouter(ReadmissionDashboard));